import React from 'react'
import { MutationUpdaterFn } from '@apollo/client'

import {
  ClearPlanMutation,
  PlanDocument,
  PlanGroupsDocument,
  PlanGroupsQuery,
  PlanQuery,
  useClearPlanMutation
} from '../../../../generated/graphql'
import ConfirmClearPlanModal from './ConfirmClearPlanModal'
import useModal from '../../../../lib/hooks/useModal'
import useNotification from '../../../../lib/hooks/useNotification'

type ConfirmClearPlanModalWrapperProps = {
  /** ID of Plan */
  id: string
  /** Determines visibility of modal */
  isOpen: boolean
}

const ConfirmClearPlanModalWrapper = ({ id, isOpen }: ConfirmClearPlanModalWrapperProps) => {
  const update: MutationUpdaterFn<ClearPlanMutation> = (proxy, { data }) => {
    if (data?.clearPlan) {
      // Read the data from our cache for GET_PLAN_BOARD query.
      const queryData = proxy.readQuery<PlanQuery>({
        query: PlanDocument,
        variables: { id }
      })

      // Update the cached plan
      if (queryData) {
        proxy.writeQuery({
          query: PlanDocument,
          data: { ...queryData, plan: data.clearPlan },
          variables: { id }
        })
      }

      // Read the data from our cache for GET_PLAN_GROUPS query.
      const planGroupsQueryData = proxy.readQuery<PlanGroupsQuery>({
        query: PlanGroupsDocument,
        variables: { planId: id }
      })

      // Update the cached groups
      if (planGroupsQueryData) {
        proxy.writeQuery({
          query: PlanGroupsDocument,
          data: { ...planGroupsQueryData, planGroups: [] },
          variables: { planId: id }
        })
      }
    }
  }
  const { showErrorNotif } = useNotification()
  const [clearPlan, mutation] = useClearPlanMutation({
    variables: { input: id },
    update,
    onError: () => {
      showErrorNotif('Plan could not be cleared.', clearPlan)
    }
  })
  const { hideModal } = useModal()
  const onConfirm = () => {
    hideModal()
    clearPlan()
  }

  // Stay on the same page after clearing
  if (mutation.data && mutation.data.clearPlan) {
    return null
  }

  return <ConfirmClearPlanModal isOpen={isOpen} onConfirm={onConfirm} />
}

export default ConfirmClearPlanModalWrapper
